import { AuthErrorAlert, AuthLayout } from '@/ui'
import { useOrganisationIndex } from '@jobilla/nexus-sdk'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { CreateOrganizationForm, OrganizationList } from '../../../components'
import { useOpenOrganisation, usePositionStorage } from '../../../hooks'
import { logger } from '../../../ui'

export const Route = createFileRoute('/_auth/company/')({
	validateSearch: z.object({
		openFailed: z.string().optional(),
	}),
	component: () => CompanyIndex(),
})

function CompanyIndex() {
	const { t } = useTranslation('auth')
	const { openFailed } = Route.useSearch()
	const openOrganisation = useOpenOrganisation()
	const { getCompanyUrl } = usePositionStorage()
	const { data: organisations, isLoading } = useOrganisationIndex({})
	const [showCreate, setShowCreate] = useState(false)
	// The org switch refetches the list, so a first organisation would otherwise flash the list.
	const [isEnteringCreated, setIsEnteringCreated] = useState(false)

	const hasOrgs = !isLoading && organisations && organisations.length > 0
	const canShowList = hasOrgs && !isEnteringCreated
	const failedOrganisationName = openFailed
		? organisations?.find((org) => org.slug === openFailed)?.name ?? openFailed
		: null

	const handleSelectOrg = async (org: { id: string; slug: string; workosId: string | null }) => {
		logger.debug('Selected organization:', org)
		return openOrganisation(org, getCompanyUrl(org.slug))
	}

	const handleCreated = async (org: { id: string; slug: string; workosId: string | null }) => {
		setIsEnteringCreated(true)
		try {
			if (!await handleSelectOrg(org)) {
				setShowCreate(false)
			}
		} finally {
			setIsEnteringCreated(false)
		}
	}

	if (isLoading) {
		// @TODO: @riz loader state for this one
		return null
	}

	return (
		<AuthLayout>
			{failedOrganisationName && !isEnteringCreated
				? (
					<div className="px-8 pt-8">
						<AuthErrorAlert
							message={t('openOrganization.switchFailed', { name: failedOrganisationName })}
						/>
					</div>
				)
				: null}
			{canShowList && !showCreate
				? (
					<OrganizationList
						onSelect={(org) => void handleSelectOrg(org)}
						onCreate={() => setShowCreate(true)}
					/>
				)
				: (
					<>
						<CreateOrganizationForm onCreated={handleCreated} />
						{canShowList
							? (
								<div className="px-8">
									<button
										type="button"
										onClick={() => setShowCreate(false)}
										className="w-full py-3 text-sm text-gray-500 hover:text-gray-800 transition-colors"
									>
										&larr; Back to organizations
									</button>
								</div>
							)
							: null}
					</>
				)}
		</AuthLayout>
	)
}
